/**
 * useFontManagerDialog — Track 3 Phase 3
 *
 * Owns the FontManagerDialog visibility flag extracted from `App.tsx`
 * as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns a single `isOpen` flag.
 *   - Exposes `open()` for the SessionShellCard "SHX/TTF шрифты" button
 *     and the TextStyleEditorDialog's "Manager..." callback.
 *   - Exposes `close()` for the dialog's onClose callback.
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: { onClose },
 *     open,
 *     close,
 *   }
 *
 * AutoCAD parity:
 *   AutoCAD uses the `STYLE` command for text styles and a separate Font
 *   Manager (Express Tools / OPTIONS Files tab). Our combined dialog
 *   keeps that separation: STYLE → TextStyleEditorDialog and Font
 *   Manager → FontManagerDialog (handled here).
 */

import { useDialogOpenState } from "./useDialogOpenState";

export interface UseFontManagerDialogResult {
  /** Whether to mount `<FontManagerDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<FontManagerDialog>`. */
  props: {
    onClose: () => void;
  };
  /** Imperative open — used by SessionShellCard / TextStyleEditorDialog. */
  open: () => void;
  /** Imperative close — same as `props.onClose`, exposed for symmetry. */
  close: () => void;
}

export function useFontManagerDialog(): UseFontManagerDialogResult {
  const { isOpen, open, close } = useDialogOpenState();
  return {
    isOpen,
    props: { onClose: close },
    open,
    close,
  };
}
